import numeral from 'numeral';
import { DailyNavSecondaryWindowSummary } from 'api';

// Shared by the dialog and the panels split out of it, so a figure cannot be
// formatted one way in the table and another way in the summary beneath it.

export const toNumber = (value: string | null): number | null => {
  if (value === null || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Same format as the grid's delta cells, so the dialog and the row agree.
export const formatSignedPercent = (value: string | null): string => {
  const parsed = toNumber(value);
  return parsed == null ? '—' : `${parsed > 0 ? '+' : ''}${(parsed * 100).toFixed(1)}%`;
};

export const formatPrice = (value: string | null, currency: string | null): string => {
  const parsed = toNumber(value);
  if (parsed == null) {
    return '—';
  }
  const formatted = numeral(parsed).format('0,0.00');
  return currency ? `${formatted} ${currency}` : formatted;
};

// Structural so any threshold-bearing summary reads the same way, rather than
// an override being explained differently in two places.
export type ThresholdBearing = Pick<
  DailyNavSecondaryWindowSummary,
  'threshold' | 'threshold_source' | 'firm_threshold'
>;

export const formatThreshold = (summary: ThresholdBearing): string => {
  const parsed = toNumber(summary.threshold);
  if (parsed == null) {
    return '—';
  }
  const effective = `${(parsed * 100).toFixed(2)}%`;
  // Mirrors the comp dialog: `threshold` is the effective value, so an override needs
  // the firm default alongside it to be judgeable.
  const firmDefault = toNumber(summary.firm_threshold);
  if (summary.threshold_source === 'company' && firmDefault != null) {
    return `${effective} (company override; firm default ${(firmDefault * 100).toFixed(2)}%)`;
  }
  const source = summary.threshold_source === 'none' ? '' : ` (${summary.threshold_source})`;
  return `${effective}${source}`;
};
